// ---------------------------------------------------------------------------
// Template → Service Category mapping (dynamic, driven by the live RFI list)
//
// Templates from HyperComply do NOT carry a service_category field, so we
// resolve it client-side: strip the "Diligent " prefix from the template
// name and look it up in the RFI list (`UseGetRfiListForFilters`). The
// matched RFI item carries `rfi_grouping` (the group/category bucket) and
// `rfi_color_code_*` (the dot color).
//
// Anything that doesn't follow the "Diligent <RFI name>" convention falls
// into the "Custom" group at the bottom of the dropdown.
// ---------------------------------------------------------------------------

const DILIGENT_PREFIX = "Diligent ";
const FOUNDATION_GCP = "Foundation - GCP";
const RECRUITMENT_SCREENING_RETENTION = "Recruitment, Screening & Retention";

// Both run on a trimmed candidate, so no trailing whitespace is expected.
// A bare trailing GxP token is an edition marker on the template name
// ("CRO Oversight GCP"); dropped so the label matches its service category.
const BARE_GXP_SUFFIX = /\s(GCP|GCLP|GLP|GMP|GVP|GDP|GAMP)$/i;
// A GxP token joined with a dash is part of the service category's own name
// ("Bioanalytical - GLP" under Good Laboratory Practice) and stays.
const DASHED_GXP_SUFFIX = /-\s*(GCP|GCLP|GLP|GMP|GVP|GDP|GAMP)$/i;

/**
 * Template / document / legacy service-key labels → canonical dbo.RFIs.rfi_name.
 * Keys are normalized via normalizeTemplateCandidate (Diligent prefix stripped, RFI/VQQ suffix removed).
 */
const TEMPLATE_TO_SERVICE_CATEGORY = {
  // Categories the catalog renamed or retired in September 2026, each mapped to the
  // category that carries on (the backend's TemplateMapping keeps the same list).
  // VQQ titles, documents and capabilities written before the change keep the old
  // name, and would otherwise show it or fall into the "Custom" bucket.
  "Foundation - All": FOUNDATION_GCP,
  // The same catalog row's name before Foundation - All.
  "Core Capabilities": FOUNDATION_GCP,
  "Foundation - Business": "Foundation - GCP Business",
  "Foundation - Quality": "Foundation - GCP Quality",
  "Foundation - Technology": "Foundation - GCP Technology",
  "Patient Recruitment & Feasibility": RECRUITMENT_SCREENING_RETENTION,
  // Older VQQ titles for the same template: "Diligent Patient Recruitment RFI".
  "Patient Recruitment": RECRUITMENT_SCREENING_RETENTION,
  "Recruitment & Retention": RECRUITMENT_SCREENING_RETENTION,
  "Patient Identification & Support Programs":
    "Patient Identification & Pre-Consent Support",
  // HyperComply can list this template without the "Diligent " prefix; the key is
  // what restores it (normalizeQuestionnaireTemplateLabel). It is its own category.
  "Foundation - GMP": "Foundation - GMP",
  "CRO Oversight": "CRO Oversight",
  "IRB/IEC": "IRB/IEC",
  "Regulatory Affairs": "Regulatory Affairs",
  "Data Protection & Privacy": "Data Protection & Privacy",
  // TemplateMapping legacy service keys (document_name on RFIDoc/Create)
  "Bioanalytical Lab": "Bioanalytical",
  "Biomarker Lab": "Biomarker",
  "ECG/EKG/EEG": "ECG",
  "Medical Imaging": "Imaging",
  "CRO Data Management": "Data Management",
  "DCT Management": "Decentralized Trials",
  "Investigator Site Budget/Contract/Payment": "Investigator Site Budgets",
  "CRO Project Management": "Project Management",
  "CRO Clinical Monitoring": "Site Monitoring - CRA",
  Biostatistics: "Statistical Programming & Biostatistics",
  "CRO Medical Monitoring": "Medical Monitoring",
  "Telemedicine/Telehealth": "Telemedicine",
  "COA/eCOA": "eCOA/ePRO",
  eConsent: "Informed Consent",
  IxRS: "RTSM",
  eTMF: "Trial Master File",
  "Wearable Sensor Device": "Wearables (All Types)",
  // Investigational Product templates (Clinical IMP Management, Clinical Supply
  // Logistics, Distribution & Warehouse Services, General Clinical IMP, Testing &
  // Release (incl. QP)) are each their own service category, so they carry no
  // override here. They previously pointed at retired parent names, which is what
  // put a stale category on their badges.
  // Catalog RFI title variants (and vs &, wording differences)
  "Investigational and Medicinal Product Management":
    "Investigational & Medicinal Product Management",
  // Retired names. Existing rows and documents still carry the old value, so map
  // them forward rather than letting them fall through to the "Custom" bucket.
  "Depot Services & Clinical Supply Logistics": "Clinical Supply Logistics",
  "Retention & Archival (Paper & Electronic)": "Retention & Archival",
  // The one template whose name differs from its service category. The badge shows
  // the service category, so without this the template name falls through to "Custom".
  "Medical Review & Physician Qualification": "Medical Review & PA Qualification",
};

const normalizeTemplateCandidate = (templateLabel) => {
  if (!templateLabel) return null;
  let candidate = templateLabel.trim();
  if (candidate.startsWith(DILIGENT_PREFIX)) {
    candidate = candidate.slice(DILIGENT_PREFIX.length).trim();
  }
  const vqqIdx = candidate.search(/\s+VQQ\b/i);
  const rfiIdx = candidate.search(/\s+RFI\b/i);
  const cutAt = [vqqIdx, rfiIdx].filter((i) => i >= 0);
  if (cutAt.length > 0) {
    candidate = candidate.slice(0, Math.min(...cutAt)).trim();
  }
  // Preserve known alias keys (e.g. "Foundation - GMP") before stripping a trailing
  // GxP token such as "... CRO Oversight GCP".
  if (TEMPLATE_TO_SERVICE_CATEGORY[candidate]) {
    return candidate;
  }
  if (!DASHED_GXP_SUFFIX.test(candidate)) {
    candidate = candidate.replace(BARE_GXP_SUFFIX, "").trim();
  }
  return candidate || null;
};

// Strip "Diligent " from a template name and return the candidate SC name
export const stripDiligentPrefix = (templateLabel) => {
  if (!templateLabel || !templateLabel.startsWith(DILIGENT_PREFIX)) return null;
  return templateLabel.slice(DILIGENT_PREFIX.length).trim();
};

/**
 * HC questionnaire title: "Diligent - {template} - {date} - {location}".
 * Template labels already include "Diligent " for standard templates — strip once.
 */
export const buildVqqRequestName = (templateLabel, datePart, locationPart) => {
  const label = (templateLabel || "").trim();
  const templatePart = stripDiligentPrefix(label) ?? label;
  return `Diligent - ${templatePart} - ${datePart} - ${locationPart}`;
};

const resolveServiceCategoryName = (templateLabel) => {
  const candidate = normalizeTemplateCandidate(templateLabel);
  if (!candidate) return null;
  if (TEMPLATE_TO_SERVICE_CATEGORY[candidate]) {
    return TEMPLATE_TO_SERVICE_CATEGORY[candidate];
  }
  const lower = candidate.toLowerCase();
  for (const [key, value] of Object.entries(TEMPLATE_TO_SERVICE_CATEGORY)) {
    if (key.toLowerCase() === lower) return value;
  }
  return candidate;
};

/** Map HC / document titles to canonical catalog rfi_name (ticket 14245). */
export const resolveServiceCategoryNameFromLabel = resolveServiceCategoryName;

/**
 * HyperComply sometimes returns sponsor templates without the "Diligent " prefix
 * (e.g. "Foundation - GMP"). Normalize to the sponsor portal title expected by
 * the UI and backend TemplateMapping (ticket 14245).
 */
export const normalizeQuestionnaireTemplateLabel = (rawName) => {
  const trimmed = (rawName || "").trim();
  if (!trimmed || trimmed.startsWith(DILIGENT_PREFIX)) return trimmed;

  const candidate = normalizeTemplateCandidate(trimmed);
  if (candidate && TEMPLATE_TO_SERVICE_CATEGORY[candidate]) {
    return `${DILIGENT_PREFIX}${candidate}`;
  }
  return trimmed;
};

/**
 * Resolve a questionnaire template option for a vendor profile service category.
 * Matches by capability rfi_id first, then by stripped template label vs rfi_name.
 * A category stored under a retired name matches the template of the category that
 * replaced it.
 */
export const findTemplateForServiceCategory = (
  categoryName,
  templateOptions = [],
  capability = null,
) => {
  const name = (categoryName || "").trim();
  if (!name) return null;
  const lower = (resolveServiceCategoryName(name) || name).toLowerCase();
  if (capability?.rfi_id) {
    const byId = templateOptions.find(
      (t) =>
        String(t.value) === String(capability.rfi_id) ||
        String(t.owner_id) === String(capability.rfi_id),
    );
    if (byId) return byId;
  }
  return (
    templateOptions.find((t) => {
      const resolved = resolveServiceCategoryName(t.label);
      return resolved && resolved.toLowerCase() === lower;
    }) || null
  );
};

/**
 * Find the RFI entry whose name matches a template's SC candidate.
 * Case-insensitive exact-name match.
 */
const findRfiForTemplate = (templateLabel, rfiList) => {
  const candidate = resolveServiceCategoryName(templateLabel);
  if (!candidate || !rfiList || rfiList.length === 0) return null;
  const lower = candidate.toLowerCase();
  return (
    rfiList.find((r) => (r?.rfi_name || "").toLowerCase() === lower) || null
  );
};

/**
 * Resolve the group name for a template from the live RFI list.
 * Returns null if the template can't be mapped (will go to Custom bucket).
 */
export const getTemplateGroup = (templateLabel, rfiList) => {
  const rfi = findRfiForTemplate(templateLabel, rfiList);
  if (!rfi) return null;
  // rfi_grouping is semicolon-separated (e.g. "CRO;Technology") — take first
  const grouping = (rfi.rfi_grouping || "").split(";").map((g) => g.trim()).filter(Boolean);
  return grouping[0] || null;
};

/**
 * Resolve the inline-style colors for a template from the matched RFI.
 * Returns { bg, text, border } hex strings, or a neutral gray fallback.
 */
export const getTemplateColors = (templateLabel, rfiList) => {
  const rfi = findRfiForTemplate(templateLabel, rfiList);
  if (!rfi) {
    return { bg: "#F3F4F6", text: "#6B7280", border: "#9CA3AF" };
  }
  return {
    bg: rfi.rfi_color_code_bg || "#EEF2FF",
    text: rfi.rfi_color_code_text || "#4338CA",
    border: rfi.rfi_color_code_border || "#4338CA",
  };
};

/**
 * Group a flat list of template options using the live RFI list.
 *
 * Group order is derived from the RFI list itself: every distinct group
 * encountered in `rfi_grouping` (in the order it first appears in the
 * RFI list), with "Foundation" pinned first if present, then any custom
 * bucket appended at the end.
 *
 * @param {Array<{value, label, ...}>} options - flat template list
 * @param {Array} rfiList - raw RFI list from UseGetRfiListForFilters
 * @returns {Array} flat list with isGroupHeader entries between groups
 */
export const buildGroupedTemplateOptions = (options, rfiList) => {
  if (!options || options.length === 0) return [];

  // Bucket templates by resolved group
  const grouped = {};
  const customBucket = [];

  options.forEach((opt) => {
    const group = getTemplateGroup(opt.label, rfiList);
    if (group) {
      if (!grouped[group]) grouped[group] = [];
      grouped[group].push(opt);
    } else {
      customBucket.push(opt);
    }
  });

  // Derive group order from rfiList: first appearance per group, Foundation first
  const groupOrder = [];
  const seenGroups = new Set();
  (rfiList || []).forEach((rfi) => {
    (rfi?.rfi_grouping || "")
      .split(";")
      .map((g) => g.trim())
      .filter(Boolean)
      .forEach((g) => {
        if (!seenGroups.has(g)) {
          seenGroups.add(g);
          groupOrder.push(g);
        }
      });
  });
  groupOrder.sort((a, b) => {
    if (a === "Foundation") return -1;
    if (b === "Foundation") return 1;
    return 0;
  });

  const result = [];
  const groupMembers = {};

  const pushGroup = (groupName, members) => {
    const sorted = [...members].sort((a, b) =>
      (a.label || "").localeCompare(b.label || "")
    );
    groupMembers[groupName] = sorted.map((o) => o.value);
    result.push({
      value: `__group__${groupName}`,
      label: groupName,
      isGroupHeader: true,
      groupName,
    });
    sorted.forEach((opt) => {
      const rfi = findRfiForTemplate(opt.label, rfiList);
      const category =
        rfi?.rfi_name || resolveServiceCategoryName(opt.label) || null;
      result.push({
        ...opt,
        colors: getTemplateColors(opt.label, rfiList),
        groupName,
        category,
      });
    });
  };

  // 1. Render groups in derived order
  groupOrder.forEach((group) => {
    if (grouped[group]) pushGroup(group, grouped[group]);
  });

  // 2. Catch any group present in templates but missing from groupOrder
  Object.keys(grouped).forEach((group) => {
    if (!groupOrder.includes(group)) pushGroup(group, grouped[group]);
  });

  // 3. Custom group at the bottom (everything that didn't map)
  if (customBucket.length > 0) {
    pushGroup("Custom", customBucket);
  }

  result.forEach((opt) => {
    if (opt.isGroupHeader) opt.groupMembers = groupMembers[opt.groupName] || [];
  });

  return result;
};
